import { type Appearance, Preferences } from '@beyond-js/ui';

/** What a person chose to look at, as the account and this interface name it */
export type Choice = Appearance;

/** The languages Accounts ships, for the shared helper */
const locales = ['en', 'es'];

/**
 * This browser's copy of a person's language and appearance, held by the family's `Preferences`
 * (decision D07).
 *
 * Accounts is where the account's values are changed, so it differs from every other product in one
 * way: its own controls write the account (`PATCH /v1/account`) and apply what the service answered.
 * Everywhere else in the family a change stays on the device. Following the system is Accounts'
 * default, so an account that never chose an appearance (`null`) follows the system here.
 *
 * The copy under `beyond-accounts` holds the two values in effect and the account's values last
 * applied here (`account`), never an account identifier. The keys of the previous interface
 * (`beyond-accounts-appearance`, `beyond-accounts-language`) are carried over once and removed.
 * Storage can throw — a private window, blocked site data — so every access is guarded.
 */
export class Device {
	static KEY = 'beyond-accounts';
	static FALLBACK = { appearance: 'system', locale: 'en' } as const;
	static LEGACY = { appearance: 'beyond-accounts-appearance', locale: 'beyond-accounts-language' } as const;

	#storage: Storage | null;
	#preferences: Preferences;
	get preferences() {
		return this.#preferences;
	}

	#remembered?: string;
	/** The language this browser holds a copy of, before any account is read; undefined when none */
	get remembered() {
		return this.#remembered;
	}

	constructor(storage: Storage | null = Device.#local()) {
		this.#storage = storage;
		this.#preferences = new Preferences({ key: Device.KEY, fallback: Device.FALLBACK, locales, storage });
	}

	/**
	 * First paint: the device copy, or following the system in English. Called once, before rendering.
	 */
	restore(): void {
		this.#remembered = this.#saved();
		this.#preferences.restore();
		this.#carry();
	}

	/** The locale in the device copy, if there is one. The format is the helper's `{appearance, locale}` */
	#saved(): string | undefined {
		try {
			const saved = JSON.parse(this.#storage?.getItem(Device.KEY) ?? 'null') as { locale?: unknown } | null;
			return typeof saved?.locale === 'string' && locales.includes(saved.locale) ? saved.locale : undefined;
		} catch {
			return undefined;
		}
	}

	/** Moves the previous interface's two keys into the shared copy, once, unless a copy already exists */
	#carry(): void {
		try {
			const appearance = this.#storage?.getItem(Device.LEGACY.appearance) ?? null;
			const locale = this.#storage?.getItem(Device.LEGACY.locale) ?? null;
			if (appearance === null && locale === null) return;

			if (this.#storage?.getItem(Device.KEY) === null) {
				const values: { appearance?: Choice; locale?: string } = {};
				if (appearance === 'system' || appearance === 'light' || appearance === 'dark') values.appearance = appearance;
				if (locale !== null && locales.includes(locale)) values.locale = this.#remembered = locale;
				this.#preferences.choose(values);
			}
			this.#storage?.removeItem(Device.LEGACY.appearance);
			this.#storage?.removeItem(Device.LEGACY.locale);
		} catch {
			// A browser that cannot be read keeps the defaults
		}
	}

	static #local(): Storage | null {
		try {
			return globalThis.localStorage ?? null;
		} catch {
			return null;
		}
	}
}

/** The one copy this interface uses, restored by `main.tsx` before the first render */
export const device = new Device();
